import {
  getHourlyPowerDiff,
  getPeakLoadWindows,
  useElectricityPricesDataQuery,
  usePeakLoadWindowsQuery
} from '@domain/electricity'
import {useElectricityQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Stack, Collapse} from '@mui/material'
import {
  ECChartLegend,
  ElectricityChart,
  EnergyProviderButton,
  PeakLoadWindowAlert
} from '@widgets/energy-consumption-chart'
import {
  getLocalizedDayMonthTimeFormat,
  useCurrentTime,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, ProductionPlanningLSSettingsKeys} from '../../../consts'
import {useLocalStorageSettings, usePermission} from '../../../hooks'
import {CollapsibleCard} from '../../shared'

import {SubmitPlanSection} from './SubmitPlanSection'

interface PlanningElectricityChartCardProps {
  startOfPlan: Moment
  endOfPlan: Moment
  hoursList: Moment[]
  cellWidth: number
  cellsDisplayed: number
  labelColumnWidth: number
}

const _PlanningElectricityChartCard: React.FC<PlanningElectricityChartCardProps> = ({
  startOfPlan,
  endOfPlan,
  hoursList,
  cellWidth,
  labelColumnWidth,
  cellsDisplayed
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const {plantId} = useUpmPlantContext()
  const now = useCurrentTime({timezoneId})
  const {visibleHoursList} = usePlanningChartStartEnd()

  const canChangeElectricity = usePermission('CHANGE_ELECTRICITY_ORDER')
  const canPurchaseElectricity =
    useCommonFeatureFlag(FeatureFlagKey.ElectricityPurchasing, {plantId}) ?? false
  const {data: electricity} = useElectricityQuery({
    plantId,
    timezoneId,
    timeFrame: [startOfPlan, endOfPlan]
  })
  const {data: electricityPrices} = useElectricityPricesDataQuery({
    plantId,
    timezoneId,
    range: [startOfPlan, endOfPlan]
  })
  const {data: peakLoadWindows} = usePeakLoadWindowsQuery({
    plantId,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const powerConsumptionQuarterly = electricity?.planned

  const actualPrices = electricityPrices?.actual ?? []
  const actualPricesEnd = actualPrices.length
    ? actualPrices[actualPrices.length - 1].datetime
    : undefined

  const peakLoadWindowsData = useMemo(
    () =>
      powerConsumptionQuarterly && peakLoadWindows
        ? getPeakLoadWindows({
            peakLoadWindows,
            powerConsumption: powerConsumptionQuarterly
          })
        : undefined,
    [peakLoadWindows, powerConsumptionQuarterly]
  )

  const [energyPurchased, energyUpForPurchase, totalEnergyPurchased] = useMemo(
    () =>
      canPurchaseElectricity
        ? [electricity?.purchased, electricity?.upForPurchasing, electricity?.submitted]
        : [],
    [electricity, canPurchaseElectricity]
  )

  const [minValue, maxValue] = useMemo(() => {
    const powerValues = [
      ...(powerConsumptionQuarterly ?? []).map((item) => item.power),
      ...(totalEnergyPurchased ?? []).map((item) => item.power)
    ]
    return powerValues ? [Math.min(...powerValues), Math.max(...powerValues)] : [0, 1]
  }, [powerConsumptionQuarterly, totalEnergyPurchased])

  const hourlyDiff = useMemo(
    () =>
      powerConsumptionQuarterly && totalEnergyPurchased
        ? getHourlyPowerDiff({
            hours: hoursList,
            purchasedQuarterlyPower: totalEnergyPurchased,
            plannedQuarterlyPower: powerConsumptionQuarterly
          })
        : undefined,
    [powerConsumptionQuarterly, totalEnergyPurchased, hoursList]
  )

  const {settings: electricityChartSettings, updateSettings: setElectricityChartSettings} =
    useLocalStorageSettings(ProductionPlanningLSSettingsKeys.PlanningElectricityChartSettings, {
      isElectricityChartOpen: true
    })
  const {isElectricityChartOpen} = electricityChartSettings

  const toggleIsElectricityChartMinimized = () =>
    setElectricityChartSettings({isElectricityChartOpen: !isElectricityChartOpen})

  const electricityChartWidth = cellWidth * cellsDisplayed
  const electricityChartHeight = maxValue > 2 ? 200 : 100

  const electricitySubmittedAt = electricity?.lastSubmitted?.submittedAt
  const electricitySubmittedBy = electricity?.lastSubmitted?.submittedBy

  return (
    <CollapsibleCard
      isCollapsed={!isElectricityChartOpen}
      onToggleCollapsed={toggleIsElectricityChartMinimized}
      title={t('planning.mwPlanned')}
      subtitle={
        electricitySubmittedAt &&
        electricitySubmittedBy &&
        t('planning.lastSubmitted', {
          date: getLocalizedDayMonthTimeFormat(
            moment.utc(electricitySubmittedAt),
            timezoneId,
            language
          ),
          name: electricitySubmittedBy
        })
      }
      headerContent={
        <Stack direction="row" spacing={2} sx={{justifyContent: 'flex-end'}}>
          {canPurchaseElectricity && (
            <EnergyProviderButton startOfPlan={startOfPlan} endOfPlan={endOfPlan} />
          )}
          <SubmitPlanSection
            isSubmitAvailable={canPurchaseElectricity && canChangeElectricity}
            electricity={electricity}
            startOfPlan={startOfPlan}
            endOfPlan={endOfPlan}
          />
        </Stack>
      }
      {...dataTestId('electricity_chart_card')}
    >
      <Stack sx={{position: 'relative'}} spacing={2}>
        <Collapse in={isElectricityChartOpen}>
          <Stack spacing={3}>
            <PeakLoadWindowAlert
              peakLoadWindows={peakLoadWindows}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
            />
            <ECChartLegend showPurchasedInfo={canPurchaseElectricity} />
          </Stack>
        </Collapse>
        <ElectricityChart
          hoursList={hoursList}
          visibleHoursList={visibleHoursList}
          now={now}
          cellWidth={cellWidth}
          yAxisWidth={labelColumnWidth}
          visibleChartWidth={electricityChartWidth}
          height={electricityChartHeight}
          powerQuarterly={powerConsumptionQuarterly}
          energyPurchased={energyPurchased}
          energyUpForPurchase={energyUpForPurchase}
          maxValue={maxValue}
          minValue={minValue}
          peakLoadWindows={peakLoadWindowsData}
          actualPricesEnd={actualPricesEnd}
          hourlyDiff={hourlyDiff}
          isMinimized={!isElectricityChartOpen}
        />
      </Stack>
    </CollapsibleCard>
  )
}

export const PlanningElectricityChartCard = React.memo(_PlanningElectricityChartCard)
